import type { HttpRequestConfig } from "@kalodata/http";
import { clipXhrHttpClient } from "@apis/http/createClipHttpClient";
import { routeMeta } from "@apis/generated/routeMeta";
import type { ResponseOf } from "@apis/http/typedRoute";
import type { ClipHttpContext, ClipHttpMeta } from "@apis/http/types";
import { DEFAULT_CLIP_TIMEOUT_MS } from "@apis/http/httpPlugins";
import { isLocalDev, VIDEO_UPLOAD_CDN_URL } from "@config";

export type UploadVideoResult = NonNullable<ResponseOf<"POST/api/user-assets/upload-video">>;

/**
 * 上传 mp4/mov 视频文件，最大 100MB / 120 秒。
 *
 * @param file 视频文件
 * @param onProgress 0-100 的上传百分比回调（与 `ClipUpload` 的 onProgress 协议一致）
 * @param signal 取消上传用的标准 AbortSignal
 */
export const uploadVideo = (
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<UploadVideoResult> => {
  const meta = routeMeta["POST/api/user-assets/upload-video"];
  const formData = new FormData();
  formData.append("video", file);

  const config: HttpRequestConfig<unknown, ClipHttpMeta, ClipHttpContext> = {
    baseUrl: isLocalDev ? "" : VIDEO_UPLOAD_CDN_URL,
    method: meta.method,
    url: meta.path,
    body: formData,
    timeoutMs: DEFAULT_CLIP_TIMEOUT_MS * 5,
    signal,
    onUploadProgress: onProgress
      ? (event) => {
          if (!event.lengthComputable) return;
          onProgress(Math.round(event.percent));
        }
      : undefined,
  };

  return clipXhrHttpClient.request<UploadVideoResult>(config);
};
